'use client';
import { useCallback, useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { CardWithTitle } from '@/components/features/CardWithTitle/CardWithTitle';
import { ShowMoreButton } from '@/components/ui/ShowMoreButton/ShowMoreButton';
import { Pagination } from '@/components/features/Pagination/Pagination';
import { Loader } from '@/components/ui/Loader/Loader';
import { getNewsList } from '@/api/news/news';
import { NewsItem } from '@/api/news/news.types';
import s from './News.module.scss';

interface Props {
  currentPage: number;
}

const PER_PAGE = 9;

export const News = ({ currentPage }: Props) => {
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const { replace } = useRouter();
  const [totalPages, setTotalPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [cards, setCards] = useState<NewsItem[]>([]);

  useEffect(() => {
    const params = new URLSearchParams(searchParams);
    params.delete('show_more');
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, []);

  useEffect(() => {
    const fetchDoctors = async () => {
      if (searchParams.get('show_more') === 'true') return;

      setIsLoading(true);

      const { items: news, pages } = await getNewsList({
        page: currentPage,
        per_page: PER_PAGE,
      });

      if (!news) {
        setIsLoading(false);
        return;
      }

      setCards(news);
      setTotalPages(pages);
      setIsLoading(false);
    };

    fetchDoctors();
  }, [searchParams]);

  const showMore = useCallback(async () => {
    if (currentPage >= totalPages) return;

    const params = new URLSearchParams(searchParams);
    const nextPage = currentPage + 1;

    params.set('show_more', String(true));
    params.set('page', String(nextPage));

    const { items: news } = await getNewsList({
      page: nextPage,
      per_page: PER_PAGE,
    });

    setCards((prev) => [...prev, ...news]);
    replace(`${pathname}?${params.toString()}`, { scroll: false });
  }, [totalPages, currentPage]);

  return (
    <>
      {isLoading && <Loader fixed />}

      <section className={s.news}>
        <div className="custom-container">
          <ul className={s.news__list}>
            {cards.map((item) => (
              <CardWithTitle
                key={item.id}
                href={`news/${item.slug ?? item.id}`}
                date={item.publication_date}
                title={item.title}
                image={item.main_image}
              />))}
          </ul>
          {cards.length > 0 && currentPage < totalPages && (
            <ShowMoreButton
              onClick={showMore}
              disabled={totalPages < 2}
              className={s.news__showMore}
            />
          )}
          <Pagination totalPages={totalPages} />
        </div>
      </section>
    </>
  );
};